import { faceNodeGetLevelId, faceNodeGetRoomId } from "building-generation/buildingGraphFilter/entityFilterSelectors";
import { getBuildingGraphPlaneTypeFromNormalToZAxisAngle } from "building-generation/generationTools/generateBuildingGraph";
import type { BuildingGraph, BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { WallPlaneSubdomain } from "building-types/walls2/wallPlane";

/** Wall Framing plan pick: coplanar regions on vertical wall planes only (not floors/roofs). */
export function buildingGraphPlanarTopologyIsVerticalPlane(topology: BuildingGraphPlanarTopology): boolean {
  return getBuildingGraphPlaneTypeFromNormalToZAxisAngle(topology.normalToZAxisAngle) === "VERTICAL";
}

/**
 * True when at least one contributor face node belongs to a room on one of the plan
 * view levels. Exterior-only or roof-only topologies are excluded.
 */
export function buildingGraphPlanarTopologyTouchesPlanViewLevels(
  building: Building,
  topology: BuildingGraphPlanarTopology,
  planLevelIds: ReadonlySet<string>,
): boolean {
  const { faceNodes } = building.buildingGraph;
  for (const faceNodeId of topology.faceNodeIds) {
    const faceNode = faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }
    if (!faceNodeGetRoomId(faceNode, building)) {
      continue;
    }
    const levelId = faceNodeGetLevelId(faceNode, building);
    if (levelId !== undefined && planLevelIds.has(levelId)) {
      return true;
    }
  }
  return false;
}

/**
 * True when the subdomain contributes at least one room-backed face node on the
 * topology that belongs to one of the plan view levels.
 */
export function wallPlaneSubdomainTouchesPlanViewLevels(
  subdomain: WallPlaneSubdomain,
  building: Building,
  topologyFaceNodeIds: ReadonlySet<string>,
  planLevelIds: ReadonlySet<string>,
): boolean {
  const { faceNodes } = building.buildingGraph;
  for (const faceNodeId of subdomain.faceNodeIds) {
    if (!topologyFaceNodeIds.has(faceNodeId)) {
      continue;
    }
    const faceNode = faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }
    if (!faceNodeGetRoomId(faceNode, building)) {
      continue;
    }
    const levelId = faceNodeGetLevelId(faceNode, building);
    if (levelId !== undefined && planLevelIds.has(levelId)) {
      return true;
    }
  }
  return false;
}

/** All vertical wall-plane topology ids (not filtered by plan view level). */
export function verticalBuildingGraphPlanarTopologyIds(building: {
  readonly buildingGraph: Pick<BuildingGraph, "faceTopologies">;
}): readonly string[] {
  const topologyIds: string[] = [];
  for (const topologyId of Object.keys(building.buildingGraph.faceTopologies)) {
    const topology = building.buildingGraph.faceTopologies[topologyId];
    if (topology && buildingGraphPlanarTopologyIsVerticalPlane(topology)) {
      topologyIds.push(topologyId);
    }
  }
  return topologyIds;
}

/** Topology ids eligible for plan pick/highlight on the given plan view levels. */
export function planLevelBuildingGraphPlanarTopologyIds(
  building: Building,
  planLevelIds: ReadonlySet<string>,
): readonly string[] {
  const topologyIds: string[] = [];
  for (const topologyId of verticalBuildingGraphPlanarTopologyIds(building)) {
    const topology = building.buildingGraph.faceTopologies[topologyId];
    if (topology && buildingGraphPlanarTopologyTouchesPlanViewLevels(building, topology, planLevelIds)) {
      topologyIds.push(topologyId);
    }
  }
  return topologyIds;
}
